import React, { useState } from 'react';
import { useWallet } from '../context/WalletContext';
import { useUserData } from '../hooks/useUserData';
import { useContracts } from '../hooks/useContracts';
import { useNotify } from '../context/NotificationContext';
import { fmtUSD, tsToDate, secondsToDHMS } from '../utils/format';
import { parseError } from '../utils/errors';
import GlassCard from '../components/common/GlassCard';
import NeonButton from '../components/common/NeonButton';
import ProgressBar from '../components/common/ProgressBar';
import ConnectPrompt from '../components/common/ConnectPrompt';
import HexLoader from '../components/loader/HexLoader';

const Contributor = () => {
  const { account } = useWallet();
  const notify = useNotify();
  const c = useContracts();
  const { contributors, loading } = useUserData();
  const [busy, setBusy] = useState(false);
  const [txLabel, setTxLabel] = useState('');

  if (!account) return <ConnectPrompt />;

  const claim = async (cIdx) => {
    try {
      setBusy(true);
      setTxLabel('Claiming Contributor Payout');
      if (!c.write.rewards) throw new Error('Rewards contract not configured.');
      const tx = await c.write.rewards.claimContributorPayout(cIdx);
      await tx.wait();
      notify.success('Claimed', 'Contributor release sent to your wallet.');
    } catch (e) { notify.error('Claim Failed', parseError(e)); }
    finally { setBusy(false); setTxLabel(''); }
  };

  return (
    <div className="animate-fade-up">
      <div className="page-title">Contributor</div>
      <div className="page-subtitle">10X payouts released every 3 months over 5 years for your 7-Year plans.</div>

      {loading && (
        <div className="empty-state"><span className="atom-spinner sp-lg" /><div className="empty-title mt-3">Loading</div></div>
      )}

      {!loading && contributors.length === 0 && (
        <GlassCard>
          <div className="empty-state">
            <div className="empty-icon">⧗</div>
            <div className="empty-title">No Contributor Records</div>
            <div className="mt-2">Invest in the 7-Year plan to become a Contributor.</div>
          </div>
        </GlassCard>
      )}

      {contributors.map((cr) => {
        const idx = Number(cr.index);
        const released = BigInt(cr.releasedUSDT);
        const total = BigInt(cr.totalPayoutUSDT);
        const releasable = BigInt(cr.releasableUSDT);
        const nextAt = Number(cr.nextReleaseAt);
        const now = Math.floor(Date.now() / 1000);
        const secondsToNext = nextAt > now ? nextAt - now : 0;
        return (
          <div key={idx} className="contrib-card">
            <div className="contrib-head">
              <div>
                <div className="inv-title">Contributor #{idx}</div>
                <div className="mono" style={{ fontSize: 12, color: 'var(--grx-text-mute)' }}>Started {tsToDate(cr.releaseStartTime)}</div>
              </div>
              <div>
                {cr.active ? <span className="badge b-cyan">Active</span> : <span className="badge b-dim">Completed</span>}
              </div>
            </div>

            <div className="grid grid-2">
              <div className="data-row" style={{ borderBottom: 'none' }}>
                <span className="label">Investment</span>
                <span className="value">{fmtUSD(cr.investmentUSDT)}</span>
              </div>
              <div className="data-row" style={{ borderBottom: 'none' }}>
                <span className="label">Total 10X Payout</span>
                <span className="value neon">{fmtUSD(cr.totalPayoutUSDT)}</span>
              </div>
              <div className="data-row" style={{ borderBottom: 'none' }}>
                <span className="label">Released</span>
                <span className="value">{fmtUSD(cr.releasedUSDT)}</span>
              </div>
              <div className="data-row" style={{ borderBottom: 'none' }}>
                <span className="label">Releases Left</span>
                <span className="value">{String(cr.releasesRemaining)}</span>
              </div>
            </div>

            <div className="mt-2">
              <ProgressBar value={Number(released)} max={Number(total) || 1} />
              <div className="progress-labels">
                <span>Progress</span>
                <span>{total > 0n ? ((Number(released) * 100) / Number(total)).toFixed(2) : '0'}%</span>
              </div>
            </div>

            <div className="mt-3" style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="mono" style={{ color: 'var(--grx-text-dim)', fontSize: 13 }}>
                {secondsToNext > 0
                  ? <>Next release in <span style={{ color: 'var(--grx-cyan)' }}>{secondsToDHMS(secondsToNext)}</span></>
                  : releasable > 0n
                    ? <span style={{ color: 'var(--grx-neon)' }}>Ready to claim: {fmtUSD(releasable)}</span>
                    : 'No release available'}
              </div>
              <NeonButton
                onClick={() => claim(idx)}
                loading={busy}
                disabled={releasable === 0n || !cr.active}
              >
                Claim {fmtUSD(releasable)}
              </NeonButton>
            </div>
          </div>
        );
      })}

      {busy && <HexLoader label={txLabel} sub="Please confirm the transaction in your wallet." />}
    </div>
  );
};

export default Contributor;
